import { useState, useRef, useEffect } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useInvalidateNotes } from '@/hooks/useNoteFeed';
import { useToast } from '@/hooks/useToast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface NoteComposerProps {
  /** If provided, this is a reply — we tag the parent event */
  replyTo?: NostrEvent;
  /** If provided, we're editing an existing note (shows existing content) */
  editEvent?: NostrEvent;
  /** Called when the note is successfully published */
  onPublished?: (event: NostrEvent) => void;
  /** Called when cancel is clicked */
  onCancel?: () => void;
  /** Auto-focus the textarea */
  autoFocus?: boolean;
  /** Compact single-line placeholder for the feed top */
  placeholder?: string;
}

export function NoteComposer({
  replyTo,
  editEvent,
  onPublished,
  onCancel,
  autoFocus = false,
  placeholder,
}: NoteComposerProps) {
  const { user, metadata } = useCurrentUser();
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const { toast } = useToast();
  const invalidateNotes = useInvalidateNotes();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Start with edit content if editing
  const [content, setContent] = useState(editEvent?.content ?? '');

  const displayName = metadata?.name ?? metadata?.display_name ?? 'You';

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
      // Move cursor to end
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [autoFocus]);

  // Auto-resize textarea
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  if (!user) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) return;

    const tags: string[][] = [];

    if (editEvent) {
      // NIP-09 delete the old event, then publish new content
      // We can't truly "edit" in Nostr — we publish a new note and delete the old
      // For kind 1 notes, the convention is to just publish a new note.
      // We'll publish a new note here; deletion is separate.
    }

    if (replyTo) {
      // NIP-10: reply tagging
      // Find the root event (if replyTo is itself a reply)
      const rootTag = replyTo.tags.find(([n, , , marker]) => n === 'e' && marker === 'root');
      const rootId = rootTag?.[1] ?? replyTo.id;

      if (rootId === replyTo.id) {
        // Direct reply to a root note
        tags.push(['e', replyTo.id, '', 'root']);
      } else {
        // Reply to a reply — keep existing root, add reply marker
        tags.push(['e', rootId, '', 'root']);
        tags.push(['e', replyTo.id, '', 'reply']);
      }
      // Tag the parent author
      tags.push(['p', replyTo.pubkey]);
    }

    publishEvent(
      { kind: 1, content: trimmed, tags },
      {
        onSuccess: (published) => {
          setContent('');
          if (textareaRef.current) textareaRef.current.style.height = 'auto';
          invalidateNotes(user.pubkey, replyTo?.id);
          onPublished?.(published);
          toast({ title: replyTo ? 'Reply posted!' : 'Note posted!' });
        },
        onError: (err) => {
          toast({
            title: 'Failed to post',
            description: String(err),
            variant: 'destructive',
          });
        },
      },
    );
  };

  const isReply = !!replyTo;
  const isEdit = !!editEvent;

  return (
    <form onSubmit={handleSubmit} className="flex gap-3 w-full">
      <Avatar className="h-9 w-9 shrink-0 mt-0.5">
        <AvatarImage src={metadata?.picture} />
        <AvatarFallback
          className="text-xs font-bold"
          style={{ background: 'var(--gb-accent)', color: '#fff' }}
        >
          {displayName[0]?.toUpperCase() ?? '?'}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={handleChange}
          placeholder={
            placeholder ??
            (isReply ? `Reply to @${replyTo.pubkey.slice(0, 8)}…` : 'What\'s on your mind?')
          }
          rows={isReply || isEdit ? 3 : 2}
          className="resize-none text-sm w-full overflow-hidden"
          style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--gb-border)',
            color: 'var(--text-primary)',
            borderRadius: 12,
            minHeight: 60,
          }}
          disabled={isPending}
        />

        {/* Actions row — shown when there's content or it's a reply/edit */}
        {(content.trim() || isReply || isEdit) && (
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs" style={{ color: content.length > 260 ? '#f44336' : 'var(--text-secondary)' }}>
              {content.length} chars
            </span>
            <div className="flex gap-2">
              {onCancel && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onCancel}
                  disabled={isPending}
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                size="sm"
                disabled={!content.trim() || isPending}
                style={{
                  background: 'var(--gb-accent)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 20,
                  paddingLeft: 16,
                  paddingRight: 16,
                }}
              >
                {isPending ? (
                  <span className="flex items-center gap-1.5">
                    <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    Posting…
                  </span>
                ) : isReply ? 'Reply' : isEdit ? 'Update' : 'Post'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}
